import React, { forwardRef, useId, useEffect, useRef, useImperativeHandle } from "react";

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  label?: string;
  size?: "sm" | "md" | "lg";
  error?: string;
  indeterminate?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      size = "md",
      error,
      className = "",
      disabled = false,
      id,
      indeterminate = false,
      checked,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = id || generatedId;
    const hasError = !!error;
    const internalRef = useRef<HTMLInputElement>(null);

    // Expose the internal ref to parent components
    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    // Set indeterminate state via DOM API (can't be set via HTML attribute)
    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    // Size styles
    const sizeStyles = {
      sm: {
        checkbox: "w-4 h-4",
        label: "text-sm",
        icon: "w-2.5 h-2.5",
      },
      md: {
        checkbox: "w-5 h-5",
        label: "text-sm",
        icon: "w-3 h-3",
      },
      lg: {
        checkbox: "w-6 h-6",
        label: "text-base",
        icon: "w-3.5 h-3.5",
      },
    };

    const currentSize = sizeStyles[size];

    return (
      <div className={`${className}`}>
        <label
          htmlFor={checkboxId}
          className={`
            inline-flex items-center gap-2.5 cursor-pointer select-none
            ${disabled ? "cursor-not-allowed opacity-60" : ""}
          `}
        >
          <div className="relative">
            <input
              ref={internalRef}
              type="checkbox"
              id={checkboxId}
              disabled={disabled}
              checked={checked}
              className="peer sr-only"
              {...props}
            />
            <div
              className={`
                ${currentSize.checkbox}
                bg-surface-elevated
                border-2 border-arc-border-strong
                rounded-md
                transition-all duration-200
                shadow-sm
                peer-hover:border-accent-muted peer-hover:shadow-md peer-hover:shadow-[rgba(var(--arc-accent-rgb),0.1)]
                peer-focus:border-transparent peer-focus:ring-2 peer-focus:ring-accent
                peer-checked:bg-arc-gradient peer-checked:border-accent peer-checked:shadow-md peer-checked:shadow-[rgba(var(--arc-accent-rgb),0.25)]
                peer-disabled:bg-surface-tertiary peer-disabled:border-arc-border peer-disabled:shadow-none
                peer-disabled:peer-checked:bg-content-tertiary
                ${indeterminate ? "bg-arc-gradient border-accent shadow-md shadow-[rgba(var(--arc-accent-rgb),0.25)]" : ""}
                ${hasError ? "border-error" : ""}
              `}
            >
              {/* Checkmark icon - shown when checked and not indeterminate */}
              <svg
                className={`
                  ${currentSize.icon}
                  absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                  text-white
                  transition-all duration-200
                  ${checked && !indeterminate ? "opacity-100 scale-100" : "opacity-0 scale-0"}
                `}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {/* Minus icon - shown when indeterminate */}
              <svg
                className={`
                  ${currentSize.icon}
                  absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                  text-white
                  transition-all duration-200
                  ${indeterminate ? "opacity-100 scale-100" : "opacity-0 scale-0"}
                `}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 12h14"
                />
              </svg>
            </div>
          </div>

          {label && (
            <span className={`${currentSize.label} text-content-secondary font-medium`}>
              {label}
            </span>
          )}
        </label>

        {error && <p className="mt-1 text-xs text-error">{error}</p>}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;



